import { useState, useCallback, useRef } from 'react';
import { ROW_HEIGHT, getPixelsPerDay, addDays, startOfDay, formatDateISO } from './timelineUtils';
import type { TimeUnit } from './timelineUtils';
import { getContrastText } from '../../issue/IssueListView';

const LONG_PRESS_MS = 200;

interface Props {
  left: number;
  width: number;
  title: string;
  statusColor: string;
  statusCategory: string;
  issueId: string;
  issueType: string;
  startDate: string | null;
  dueDate: string | null;
  timeUnit: TimeUnit;
  onClick: () => void;
  onDateChange: (issueId: string, field: 'startDate' | 'dueDate', newDate: string) => void;
  onMoveStart: (issueId: string, issueType: string, e: React.MouseEvent) => void;
}

export default function GanttBar({
  left,
  width,
  title,
  statusColor,
  statusCategory,
  issueId,
  issueType,
  startDate,
  dueDate,
  timeUnit,
  onClick,
  onDateChange,
  onMoveStart,
}: Props) {
  const isClosed = statusCategory === 'DONE';
  const barHeight = 24;
  const topOffset = (ROW_HEIGHT - barHeight) / 2;

  const [dragState, setDragState] = useState<{
    side: 'left' | 'right';
    deltaX: number;
  } | null>(null);
  const [tooltip, setTooltip] = useState<string | null>(null);
  const [isMoveDragging, setIsMoveDragging] = useState(false);

  const pxPerDay = getPixelsPerDay(timeUnit);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Compute displayed bar position (accounting for drag)
  let displayLeft = left;
  let displayWidth = Math.max(width, 20);
  if (dragState) {
    if (dragState.side === 'left') {
      displayLeft = left + dragState.deltaX;
      displayWidth = Math.max(width - dragState.deltaX, 20);
    } else {
      displayWidth = Math.max(width + dragState.deltaX, 20);
    }
  }

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, side: 'left' | 'right') => {
      e.preventDefault();
      e.stopPropagation();

      const canResize = side === 'left' ? !!startDate : !!dueDate;
      if (!canResize) return;

      const startX = e.clientX;

      longPressTimerRef.current = setTimeout(() => {
        const handleMouseMove = (moveEvent: MouseEvent) => {
          const deltaX = moveEvent.clientX - startX;
          const deltaDays = Math.round(deltaX / pxPerDay);

          if (side === 'left' && startDate) {
            const newDate = addDays(startOfDay(new Date(startDate)), deltaDays);
            if (dueDate && newDate >= startOfDay(new Date(dueDate))) return;
            setDragState({ side: 'left', deltaX: deltaDays * pxPerDay });
            setTooltip(`${startDate.slice(0, 10)} → ${formatDateISO(newDate)}`);
          } else if (side === 'right' && dueDate) {
            const newDate = addDays(startOfDay(new Date(dueDate)), deltaDays);
            if (startDate && startOfDay(new Date(startDate)) >= newDate) return;
            setDragState({ side: 'right', deltaX: deltaDays * pxPerDay });
            setTooltip(`${dueDate.slice(0, 10)} → ${formatDateISO(newDate)}`);
          }
        };

        const handleMouseUp = (upEvent: MouseEvent) => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          setDragState(null);
          setTooltip(null);

          const deltaX = upEvent.clientX - startX;
          const deltaDays = Math.round(deltaX / pxPerDay);
          if (deltaDays === 0) return;

          if (side === 'left' && startDate) {
            const newDate = addDays(startOfDay(new Date(startDate)), deltaDays);
            if (dueDate && newDate >= startOfDay(new Date(dueDate))) return;
            onDateChange(issueId, 'startDate', newDate.toISOString());
          } else if (side === 'right' && dueDate) {
            const newDate = addDays(startOfDay(new Date(dueDate)), deltaDays);
            if (startDate && startOfDay(new Date(startDate)) >= newDate) return;
            onDateChange(issueId, 'dueDate', newDate.toISOString());
          }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }, LONG_PRESS_MS);

      const handleEarlyUp = () => {
        clearTimeout(longPressTimerRef.current);
        document.removeEventListener('mouseup', handleEarlyUp);
      };
      document.addEventListener('mouseup', handleEarlyUp);
    },
    [startDate, dueDate, pxPerDay, issueId, onDateChange],
  );

  const handleMoveMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (issueType === 'EPIC') {
        // EPIC cannot be moved; treat as click
        onClick();
        return;
      }

      longPressTimerRef.current = setTimeout(() => {
        setIsMoveDragging(true);
        onMoveStart(issueId, issueType, e);
      }, LONG_PRESS_MS);

      const handleEarlyUp = () => {
        clearTimeout(longPressTimerRef.current);
        document.removeEventListener('mouseup', handleEarlyUp);
        if (!isMoveDragging) {
          onClick();
        }
      };
      document.addEventListener('mouseup', handleEarlyUp);
    },
    [issueId, issueType, onClick, onMoveStart, isMoveDragging],
  );

  return (
    <div
      className={`absolute rounded-md border flex items-stretch text-sm transition-shadow hover:shadow-md ${
        isClosed ? 'line-through opacity-70' : ''
      } ${isMoveDragging ? 'opacity-50' : ''}`}
      style={{
        left: `${displayLeft}px`,
        width: `${displayWidth}px`,
        height: `${barHeight}px`,
        top: `${topOffset}px`,
        zIndex: dragState || isMoveDragging ? 20 : undefined,
        backgroundColor: statusColor + '33',
        borderColor: statusColor + '66',
        color: getContrastText(statusColor) === '#FFFFFF' ? statusColor : getContrastText(statusColor),
      }}
      title={title}
    >
      {/* Left resize handle */}
      <div
        className="absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-black/10 rounded-l-md z-10"
        onMouseDown={(e) => handleResizeStart(e, 'left')}
      />

      {/* Center content — move drag */}
      <div
        className="flex-1 flex items-center px-3 truncate cursor-grab active:cursor-grabbing select-none"
        onMouseDown={handleMoveMouseDown}
      >
        {displayWidth > 40 && <span className="truncate">{title}</span>}
      </div>

      {/* Right resize handle */}
      <div
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-black/10 rounded-r-md z-10"
        onMouseDown={(e) => handleResizeStart(e, 'right')}
      />

      {/* Tooltip */}
      {tooltip && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-sm rounded whitespace-nowrap z-30 pointer-events-none">
          {tooltip}
        </div>
      )}
    </div>
  );
}
